// Sidebar.tsx - Fullscreen Studio sidebar

import React from 'react';
import { Segment } from './types';
import { getSidebarStyle } from './styles';
import StudioList, { StudioTab } from './StudioList';

interface SidebarProps {
  isFullscreen: boolean;
  practiceMode: boolean;
  segments: Segment[];
  activeSegment: string | null;
  activeComboId: string | null;
  onSegmentClick: (segmentId: string) => void;
  onComboClick: (comboId: string) => void;
  editingSegment: string | null;
  segmentNameInputRef: React.RefObject<HTMLInputElement | null>;
  onSaveSegmentName: (segmentId: string, newName: string) => void;
  onStartEditingSegment: (segmentId: string) => void;
  onToggleSegmentLearned: (segmentId: string) => void;
  onToggleSegmentDisabled: (segmentId: string) => void;
  onDeleteSegment: (segmentId: string) => void;
  onBatchToggleDisabledSelected: (tab: StudioTab) => void;
  onBatchDeleteSelected: (tab: StudioTab) => void;
  onCreateCombo: () => void;
  onDuplicateCombo: (comboId: string) => void;
  onDeleteCombo: (comboId: string) => void;
  activeStudioTab: StudioTab;
  onActiveStudioTabChange: (tab: StudioTab) => void;
  isLooping: boolean;
  videoSrc?: string | null;
  onToggleAllLearned?: (learned: boolean, tab: StudioTab) => void;
  showStudioTabs?: boolean;
  headerControl?: React.ReactNode;
  playingSegmentId?: string | null;
  onCancelEditingSegment?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isFullscreen,
  practiceMode,
  segments,
  activeSegment,
  activeComboId,
  onSegmentClick,
  onComboClick,
  editingSegment,
  segmentNameInputRef,
  onSaveSegmentName,
  onStartEditingSegment,
  onToggleSegmentLearned,
  onToggleSegmentDisabled,
  onDeleteSegment,
  onBatchToggleDisabledSelected,
  onBatchDeleteSelected,
  onCreateCombo,
  onDuplicateCombo,
  onDeleteCombo,
  activeStudioTab,
  onActiveStudioTabChange,
  isLooping,
  videoSrc = null,
  onToggleAllLearned,
  showStudioTabs = true,
  headerControl,
  playingSegmentId = null,
  onCancelEditingSegment
}) => {
  const sidebarStyle = getSidebarStyle(isFullscreen, practiceMode);

  if (sidebarStyle.display === 'none') {
    return null;
  }

  return (
    <div style={sidebarStyle} className="fullscreen-sidebar">
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {/* No panel heading: the list starts straight at the Segments / Combos selector. */}
        <div style={{ flex: 1, minHeight: 0 }}>
          <StudioList
            segments={segments}
            activeSegment={activeSegment}
            activeComboId={activeComboId}
            isLooping={isLooping}
            editingSegment={editingSegment}
            segmentNameInputRef={segmentNameInputRef}
            onSaveSegmentName={onSaveSegmentName}
            onStartEditingSegment={onStartEditingSegment}
            onPlaySegment={onSegmentClick}
            onSelectCombo={onComboClick}
            onToggleSegmentLearned={onToggleSegmentLearned}
            onToggleSegmentDisabled={onToggleSegmentDisabled}
            onDeleteSegment={onDeleteSegment}
            onBatchToggleDisabledSelected={onBatchToggleDisabledSelected}
            onBatchDeleteSelected={onBatchDeleteSelected}
            onCreateCombo={onCreateCombo}
            onDuplicateCombo={onDuplicateCombo}
            onDeleteCombo={onDeleteCombo}
            activeTab={activeStudioTab}
            onActiveTabChange={onActiveStudioTabChange}
            videoSrc={videoSrc}
            onToggleAllLearned={onToggleAllLearned}
            variant="fullscreen"
            showTabs={showStudioTabs}
            headerControl={headerControl}
            playingSegmentId={playingSegmentId}
            onCancelEditingSegment={onCancelEditingSegment}
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
